import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { BookmarkItem, bookmarkItemSchema } from "@/features/bookmarks";
import { userApi } from "@/store/services/user.service";

export interface BookmarkState {
    bookmarks: BookmarkItem[];
    isInitialized: boolean;
}

const initialState: BookmarkState = { bookmarks: [], isInitialized: false };

const getStorageKey = (userId: string) => `lssa:bookmarks:${userId}`;

const updateLocalStorage = (userId: string, bookmarks: BookmarkItem[]) => {
    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(bookmarks));
};

const bookmarkSlice = createSlice({
    name: "bookmark",
    initialState,
    reducers: {
        addBookmark: (state, action: PayloadAction<{ userId: string; bookmark: BookmarkItem }>) => {
            const { userId, bookmark } = action.payload;

            if (!state.isInitialized) return;

            state.bookmarks = [bookmark, ...state.bookmarks];

            updateLocalStorage(userId, state.bookmarks);
        },
        removeBookmark: (state, action: PayloadAction<{ userId: string; id: string; type: string }>) => {
            const { userId, id, type } = action.payload;

            if (!state.isInitialized) return;

            state.bookmarks = state.bookmarks.filter((b) => !(b.type === type && b.id === id));

            updateLocalStorage(userId, state.bookmarks);
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(userApi.endpoints.getUser.matchFulfilled, (state, { payload: user }) => {
            try {
                const bookmarks = localStorage.getItem(getStorageKey(user.id));
                if (bookmarks) {
                    const data = JSON.parse(bookmarks);

                    const validation = bookmarkItemSchema.array().safeParse(data);

                    if (validation.success) {
                        state.bookmarks = validation.data;
                    } else {
                        console.error("Invalid bookmarks data in localStorage", validation.error);
                    }
                }
            } catch (error) {
                console.error("Failed to parse bookmarks data from localStorage", error);
            }
            state.isInitialized = true;
        });
    },
});

export const { addBookmark, removeBookmark } = bookmarkSlice.actions;
export const bookmarkReducer = bookmarkSlice.reducer;
